import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, EventEmitter, Output } from '@angular/core';
import { RouterModule, RouterLink } from '@angular/router';

import { FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import { PartsManagementService } from '../../../services/parts-management.service';
import { PartsManagementHeader } from '../../../models/parts-management-header.model';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { PartsManagementFilterForm } from '../../../form-model/parts-management-filter.form.model';
import { PartsManagementFilter } from '../../../models/parts-management-filter.model';
import { PartsManagementSearchTextForm } from '../../../form-model/parts-management-search-text.form.model';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
//import { SharedDataService } from '../../../services/shared-data.service';
import { PartsManagementListGridColumns } from './parts-management-grid.config';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { utils } from '../../../helper/utils';

@Component({
  selector: 'app-parts-management-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, RouterLink, LoaderComponent],
  templateUrl: './parts-management-grid.component.html',
  styleUrl: './parts-management-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PartsManagementGridComponent {
  data: PartsManagementHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: PartsManagementFilter = {};
  getPartsManagementApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    partsManagementFilterForm: FormGroup<PartsManagementFilterForm>;
    partsManagementSearchTextForm: FormGroup<PartsManagementSearchTextForm>;
  }>;
  columns = PartsManagementListGridColumns;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isPartsManagementList: boolean = true;
  constructor(private partsManagementService: PartsManagementService) {}

  ngOnInit() {
    this.urlNavigate = '/master-data/parts-management';
    this.getPartsManagement();
    this.subscribeToFormChanges();
    this.subscribeToRefresh();
  }

  private subscribeToRefresh() {
    this.partsManagementService.partsRefresh$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.currentPage = 1;
      this.getPartsManagement();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (
        this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.value ||
        this.filterFormGroup.controls.partsManagementSearchTextForm.controls.appliedPMSearchTextFilter.value
      ) {
        this.onApplyFilter();
      }
    });
  }

  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getPartsManagement();
    }
  }

  private sortColumnName(gridColumnName: string): string {
    return gridColumnName;
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getPartsManagement();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getPartsManagement(true);
    }
  }

  private convertCheckboxValuesToBooleans(values: string[] | null): boolean[] {
    if (!values || values.length === 0) {
      return [];
    }
    return values.map((val) => val === 'Y');
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.partsManagementFilterForm.valid) {
      const filters: PartsManagementFilter = {};

      if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value) {
        filters.partCodesList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partCodesList.value;
      }
      if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value) {
        filters.partGroupCodeList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partGroupCodeList.value;
      }
      if (this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value) {
        filters.partManufacturerCodeList = this.filterFormGroup.controls.partsManagementFilterForm.controls.partManufacturerCodeList.value;
      }
      if (this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value) {
        filters.description = this.filterFormGroup.controls.partsManagementFilterForm.controls.description.value;
      }

      const activeValue = this.filterFormGroup.controls.partsManagementFilterForm.controls.partActiveStatusesList.value;
      const activeValues = this.convertCheckboxValuesToBooleans(activeValue);
      if (activeValues.length > 0) {
        filters.partActiveStatusesList = activeValues as any;
      }

      const coreValue = this.filterFormGroup.controls.partsManagementFilterForm.controls.isSerialTagNumberRequired.value;
      const coreValues = this.convertCheckboxValuesToBooleans(coreValue);
      if (coreValues.length > 0) {
        filters.isSerialTagNumberRequired = coreValues as any;
      }

      const deductCoreValue = this.filterFormGroup.controls.partsManagementFilterForm.controls.deductCore.value;
      const deductCoreValues = this.convertCheckboxValuesToBooleans(deductCoreValue);
      if (deductCoreValues.length > 0) {
        filters.deductCore = deductCoreValues as any;
      }
      if (this.filterFormGroup.controls.partsManagementSearchTextForm.controls.searchText.value) {
        filters.searchText = this.filterFormGroup.controls.partsManagementSearchTextForm.controls.searchText.value;
      }

      this.filterParam = filters;
      this.currentPage = 1;
      this.getPartsManagement();
    }
  }

  getPartsManagement(isPageChange: boolean = false): void {
    this.data = [];
    this.getPartsManagementApiCallStatus = 'IN_PROGRESS';
    this.partsManagementService
      .getPartsManagement(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body.map((item) => ({
            ...item,
            id: item.partCode,
            isActive: item.isActive ? 'Y' : 'N',
            isSerialTagNumberRequired: item.isSerialTagNumberRequired ? 'Y' : 'N',
            deductCore: item.deductCore ? 'Y' : 'N',
          }));
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getPartsManagementApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: (err) => {
          this.getPartsManagementApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.partsManagementSearchTextForm.controls.appliedPMSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
  }
}
